/**
 * Legacy parity: SCMS.Controller.XMLHelpers.QueryParameterSideMenuXMLHelper
 * and labels from UserControls/QueryDefineParametersMenu.ascx
 */

export type ParameterSideMenuKey =
  | 'General'
  | 'ClaimType'
  | 'PaymentsBanks'
  | 'TimeTracking'
  | 'Examiner'
  | 'ICD9'
  | 'States'
  | 'ClaimFlags'
  | 'ExaminersOffices'
  | 'Locations'
  | 'Claims'
  | 'Financials'
  | 'AdvClaimFlags'
  | 'BankAccounts'
  | 'Payments'
  | 'AdvTimeTracking'
  | 'MajorClass'
  | 'WorkersComp'
  | 'GeneralLiability'
  | 'AutoLiability'
  | 'PropertyLiability'
  | 'Disability';

export const SIMPLE_PARAMETER_MENU_KEYS: ParameterSideMenuKey[] = [
  'General',
  'ClaimType',
  'PaymentsBanks',
  'TimeTracking',
  'Examiner',
  'ICD9',
  'States',
  'ClaimFlags',
];

export const ADVANCED_PARAMETER_MENU_KEYS: ParameterSideMenuKey[] = [
  'ExaminersOffices',
  'Locations',
  'Claims',
  'Financials',
  'AdvClaimFlags',
  'BankAccounts',
  'Payments',
  'AdvTimeTracking',
  'MajorClass',
  'WorkersComp',
  'GeneralLiability',
  'AutoLiability',
  'PropertyLiability',
  'Disability',
];

export const PARAMETER_SIDE_MENU_LABELS: Record<ParameterSideMenuKey, string> = {
  General: 'General',
  ClaimType: 'Claim Type',
  PaymentsBanks: 'Payments/Banks',
  TimeTracking: 'Time Tracking',
  Examiner: 'Examiner',
  ICD9: 'ICD',
  States: 'States',
  ClaimFlags: 'Claim Flags',
  ExaminersOffices: 'Examiners/Offices',
  Locations: 'Locations',
  Claims: 'Claims',
  Financials: 'Financials',
  AdvClaimFlags: 'Claim Flags',
  BankAccounts: 'Bank Accounts',
  Payments: 'Payments',
  AdvTimeTracking: 'Time Tracking',
  MajorClass: 'Major Class',
  WorkersComp: "Workers' Comp",
  GeneralLiability: 'General Liability',
  AutoLiability: 'Auto Liability',
  PropertyLiability: 'Property',
  Disability: 'Disability',
};

type LobKey = 'WorkersComp' | 'GeneralLiability' | 'AutoLiability' | 'PropertyLiability' | 'Disability';

const LOB_LABEL: Record<LobKey, string> = {
  WorkersComp: "Workers' Comp",
  GeneralLiability: 'General Liability',
  AutoLiability: 'Auto Liability',
  PropertyLiability: 'Property',
  Disability: 'Disability',
};

function verifyLob(key: LobKey, selectedLobs: string[], contractLobs: string[]): boolean {
  const label = LOB_LABEL[key];
  const selected = selectedLobs.some((l) => l === label);
  if (!selected) {
    return false;
  }
  return contractLobs.some((c) => c === label);
}

/** Banking queries have no LOB picker; LOB submenus follow contract coverage only (legacy parity). */
function verifyLobSideMenu(
  key: LobKey,
  queryType: string,
  selectedLobs: string[],
  contractLobs: string[],
): boolean {
  const q = (queryType || 'C').trim();
  if (q === 'B') {
    return contractLobs.some((c) => c === LOB_LABEL[key]);
  }
  return verifyLob(key, selectedLobs, contractLobs);
}

/** Legacy QueryWizardSimpleType: WC / DS claim-type rows when that LOB is on contract; claim queries also require LOB selected. */
export function isClaimTypeLobSectionVisible(
  lob: 'WorkersComp' | 'Disability',
  selectedLobs: string[],
  contractLobs: string[],
  queryType = 'C',
): boolean {
  const q = (queryType || 'C').trim();
  if (q === 'B') {
    const label = lob === 'WorkersComp' ? "Workers' Comp" : 'Disability';
    return contractLobs.some((c) => c === label);
  }
  return verifyLob(lob, selectedLobs, contractLobs);
}

function typeNeBanking(queryType: string): boolean {
  return queryType !== 'B';
}

function typeEqPaymentOrBanking(queryType: string): boolean {
  return queryType === 'A' || queryType === 'B';
}

function typeEqTime(queryType: string): boolean {
  return queryType === 'T';
}

export type ParameterSideMenuOptions = {
  selectedLobs: string[];
  contractLobs: string[];
  /** Legacy: hide Locations for agency users */
  isAgencyUser?: boolean;
};

export function isParameterSideMenuItemVisible(
  key: ParameterSideMenuKey,
  queryType: string,
  opts: ParameterSideMenuOptions,
): boolean {
  const q = (queryType || 'C').trim();
  const { selectedLobs, contractLobs, isAgencyUser = false } = opts;

  switch (key) {
    case 'General':
      return true;
    case 'ClaimType':
      return typeNeBanking(q);
    case 'PaymentsBanks':
      return typeEqPaymentOrBanking(q);
    case 'TimeTracking':
      return typeEqTime(q);
    case 'Examiner':
      return typeNeBanking(q);
    case 'ICD9':
      return typeNeBanking(q);
    case 'States':
      return typeNeBanking(q);
    case 'ClaimFlags':
      return typeNeBanking(q);
    case 'ExaminersOffices':
      return typeNeBanking(q);
    case 'Locations':
      return typeNeBanking(q) && !isAgencyUser;
    case 'Claims':
      return typeNeBanking(q);
    case 'Financials':
      return typeNeBanking(q);
    case 'AdvClaimFlags':
      return typeNeBanking(q);
    case 'BankAccounts':
      return typeEqPaymentOrBanking(q);
    case 'Payments':
      return typeEqPaymentOrBanking(q);
    case 'AdvTimeTracking':
      return typeEqTime(q);
    case 'MajorClass':
      return typeNeBanking(q);
    case 'WorkersComp':
      return verifyLobSideMenu('WorkersComp', q, selectedLobs, contractLobs);
    case 'GeneralLiability':
      return verifyLobSideMenu('GeneralLiability', q, selectedLobs, contractLobs);
    case 'AutoLiability':
      return verifyLobSideMenu('AutoLiability', q, selectedLobs, contractLobs);
    case 'PropertyLiability':
      return verifyLobSideMenu('PropertyLiability', q, selectedLobs, contractLobs);
    case 'Disability':
      return verifyLobSideMenu('Disability', q, selectedLobs, contractLobs);
    default:
      return false;
  }
}

/** Scroll targets within the General parameters step (single-page layout). */
export function getParameterSideMenuScrollAnchor(key: ParameterSideMenuKey): string {
  switch (key) {
    case 'General':
    case 'PaymentsBanks':
    case 'BankAccounts':
    case 'Payments':
    case 'MajorClass':
    case 'Locations':
      return 'qp-general';
    case 'ClaimType':
    case 'Examiner':
    case 'ICD9':
    case 'States':
    case 'ClaimFlags':
    case 'ExaminersOffices':
    case 'Claims':
    case 'AdvClaimFlags':
    case 'WorkersComp':
    case 'GeneralLiability':
    case 'AutoLiability':
    case 'PropertyLiability':
    case 'Disability':
      return 'qp-claim-filters';
    case 'TimeTracking':
    case 'AdvTimeTracking':
    case 'Financials':
      return 'qp-date-ranges';
    default:
      return 'qp-general';
  }
}
